/**
 * Shared file-type helpers for the common uploader and its adapters.
 * MIME labels here mirror the backend's grouped sets in
 * `apps/files/validation.py` and the `ACCEPT_BY_USE_CASE` picker map.
 */

export interface UploadedFileItem {
  uuid: string
  file_name: string
  file_url?: string | null
  mime_type?: string | null
}

/**
 * The loose response shape the file-upload hook resolves with. The backend has
 * historically returned either snake_case or PascalCase/camelCase keys, so the
 * contract is deliberately permissive.
 */
export interface UploadFileResult {
  uuid?: string
  UUID?: string
  file_name?: string
  fileName?: string
  file_url?: string | null
  mime_type?: string | null
  [key: string]: unknown
}

/**
 * Normalise a raw upload-hook response into the canonical {@link UploadedFileItem}
 * envelope. This is the single source of truth for reconciling the hook's
 * inconsistent key casing (`uuid`/`UUID`, `file_name`/`fileName`); callers that
 * need a stable shape (e.g. the multi-file uploader) should map through here
 * rather than re-deriving the fallbacks inline. `fallback` supplies name/mime
 * from the local File when the response omits them.
 */
export function toUploadedFileItem(
  result: UploadFileResult,
  fallback?: { name?: string; type?: string },
): UploadedFileItem {
  return {
    uuid: (result.uuid ?? result.UUID ?? '') as string,
    file_name: (result.file_name ?? result.fileName ?? fallback?.name ?? '') as string,
    file_url: (result.file_url ?? null) as string | null,
    mime_type: (result.mime_type ?? fallback?.type ?? null) as string | null,
  }
}

const MIME_LABEL: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/heic': 'HEIC',
  'image/heif': 'HEIC',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/x-msvideo': 'AVI',
  'video/x-ms-wmv': 'WMV',
  'application/vnd.ms-outlook': 'MSG',
  'message/rfc822': 'EML',
}

// Wildcard subtypes (e.g. "image/*", "video/*") name a whole family, not a
// single format - render a friendly plural rather than a bare "*".
const WILDCARD_LABEL: Record<string, string> = {
  image: 'Images',
  video: 'Videos',
  audio: 'Audio',
}

/**
 * Turn an `accept` string into a readable, de-duped list e.g. "PNG, JPG, PDF".
 *
 * Wildcard family labels ("Images"/"Videos"/"Audio") are the only user-facing
 * copy here; pass a translate fn (`t`) to localise them. Without it the English
 * fallback is used, so non-i18n call sites keep working unchanged.
 */
export function humanizeAccept(
  accept: string,
  t?: (key: string, defaultValue: string) => string,
): string {
  const seen = new Set<string>()
  const labels: string[] = []
  for (const raw of accept.split(',')) {
    const mime = raw.trim()
    if (!mime || mime === '*/*') continue
    let label: string
    if (mime.endsWith('/*')) {
      const top = mime.slice(0, -2)
      const fallback = WILDCARD_LABEL[top] || top.toUpperCase()
      label = t ? t(`fileTypes.${top}`, fallback) : fallback
    } else {
      label =
        MIME_LABEL[mime] ||
        (mime.startsWith('.')
          ? mime.slice(1).toUpperCase()
          : mime.split('/').pop()?.toUpperCase()) ||
        mime
    }
    if (!seen.has(label)) {
      seen.add(label)
      labels.push(label)
    }
  }
  return labels.join(', ')
}

export function extensionLabel(name: string | undefined): string | undefined {
  if (!name) return undefined
  const dot = name.lastIndexOf('.')
  if (dot < 0 || dot === name.length - 1) return undefined
  return name.slice(dot + 1).toUpperCase()
}

/** Short type caption for a file row, e.g. "PDF", "JPG", "XLSX". */
export function fileKindLabel(
  mime: string | null | undefined,
  name: string | undefined,
): string {
  if (mime && MIME_LABEL[mime]) return MIME_LABEL[mime]
  const ext = extensionLabel(name)
  if (ext) return ext
  if (mime) return mime.split('/').pop()?.toUpperCase() || 'FILE'
  return 'FILE'
}

export function isImageValue(
  mime: string | null | undefined,
  name: string | undefined,
): boolean {
  if (mime) return mime.startsWith('image/')
  const ext = (extensionLabel(name) || '').toLowerCase()
  return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)
}

/**
 * Hardcoded map of file `use_case` → HTML `accept` attribute, mirroring
 * `backend/apps/files/validation.py::ALLOWED_MIME_TYPES_BY_USE_CASE`. The
 * browser file picker filters to these types so the user can't choose a
 * file the backend will reject.
 *
 * TODO(file-use-cases): a fuller fix is on its way that will surface
 * this metadata from the backend instead of duplicating it here.
 */
// Reusable MIME groups, mirroring the backend's type sets in
// ``apps/files/validation.py``. Kept in sync so the picker only offers what
// the backend will actually accept.
// HEIC/HEIF (iPhone default) is accepted wherever a photo is; the backend
// transcodes it to JPEG on ingest. The `.heic` extension is included because
// some OS file pickers filter by extension, not the image/heic MIME.
const IMAGE_TYPES = 'image/jpeg,image/png,image/heic,image/heif,.heic'
const OFFICE_DOC_TYPES =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv'
const VIDEO_TYPES = 'video/mp4,video/x-msvideo,video/quicktime,video/x-ms-wmv'
// Outlook .msg and saved .eml emails, kept as claim evidence. The extensions
// are listed because pickers rarely know these MIME types.
const EMAIL_TYPES = 'application/vnd.ms-outlook,message/rfc822,.msg,.eml'
const SPREADSHEET_TYPES =
  'text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
// Most upload surfaces accept a photo OR an office document.
const DOCUMENT_OR_IMAGE = `${IMAGE_TYPES},${OFFICE_DOC_TYPES}`

const ACCEPT_BY_USE_CASE: Record<string, string> = {
  // Image-only slots.
  PRODUCT_IMAGE: IMAGE_TYPES,
  ORGANISATION_LOGO: IMAGE_TYPES,
  BRAND_LOGO: IMAGE_TYPES,
  ORGANISATION_SPLASH: IMAGE_TYPES,
  USER_PROFILE_IMAGE: IMAGE_TYPES,
  // Media / import-only slots.
  PRODUCT_VIDEO: VIDEO_TYPES,
  WIZARD_SPREADSHEET: SPREADSHEET_TYPES,
  // Supporting-document use cases: photos AND office documents.
  PRODUCT_DOCUMENT: DOCUMENT_OR_IMAGE,
  WARRANTY_TERMS: DOCUMENT_OR_IMAGE,
  WARRANTY_DOCUMENT: DOCUMENT_OR_IMAGE,
  PROOF_OF_PURCHASE: DOCUMENT_OR_IMAGE,
  PROOF_OF_OWNERSHIP: DOCUMENT_OR_IMAGE,
  CLAIM_PACKET: DOCUMENT_OR_IMAGE,
  CLAIM_CHECKLIST: DOCUMENT_OR_IMAGE,
  INSPECTION_REPORT: DOCUMENT_OR_IMAGE,
  // Field-captured evidence: documents, photos, and video.
  CLAIM_EVIDENCE: `${DOCUMENT_OR_IMAGE},${VIDEO_TYPES},${EMAIL_TYPES}`,
  INSPECTION_EVIDENCE: `${DOCUMENT_OR_IMAGE},${VIDEO_TYPES},${EMAIL_TYPES}`,
}

const DEFAULT_FILE_USE_CASE = 'PROOF_OF_PURCHASE'

export function getAcceptForUseCase(useCase: string | undefined): string {
  if (useCase && ACCEPT_BY_USE_CASE[useCase]) return ACCEPT_BY_USE_CASE[useCase]
  return ACCEPT_BY_USE_CASE[DEFAULT_FILE_USE_CASE]
}
